import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ABCP_INGEST_ACCEPT } from '../../types/closing-documents.types';
import { RejectedUpload, UploadFailure, UploadRejectionReason } from './upload-modal.types';

//REV-21363 — the shared upload modal (Pure View). Contract: e2e-lab/upload-modal.lab.spec.ts.
//
//SKELETON: the I/O below is final, the template is deliberately empty — the contract is red first.
//
//ONE component for BOTH upload entry points (ADR-0009 §11): the hub's `Upload` button and the
//account area's upload control. The only difference is `[multiple]`.
//
//Dumb by construction: it PICKS and VALIDATES, it never uploads. `upload(File[])` leaves the
//component and REV-21376 does the HTTP. The contract asserts no `HttpClient` is injected.
//
//Where ADR-0009 overrules the prototype:
//  · §12 — ingest is PDF-only. The prototype's input says `application/pdf,image/*` and its copy
//    says "PDF oder Bild"; both are narrowed to PDF here.

/** 32 MB — the cap of the existing upload route (`document-pipeline.md` §8). */
const DEFAULT_MAX_SIZE_BYTES = 32 * 1024 * 1024;

@Component({
    selector: 'abcp-upload-modal',
    templateUrl: './upload-modal.component.html',
    styleUrls: ['./upload-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpUploadModalComponent {
    /** `false` for the account area: a new pick REPLACES the selection instead of appending. */
    @Input() multiple = true;

    /**
     * PDF, Excel and Word (ADR-0009 §12 as widened by Nachtrag 3, REV-21964).
     *
     * ⚠️ `accept` is convenience, not a boundary — it is switchable in the client. The binding rule
     * lives on the server, which reads MAGIC NUMBERS and answers HTTP 415.
     *
     * ⚠️ The DEFAULT is what matters here, not the input. It stayed `'application/pdf'` while the
     * server had long accepted Excel and Word, and none of the three hosts overrode it — so the
     * headline promise of REV-21964 was unreachable through the front door and Office files could
     * only arrive via SharePoint (Code-Review Runde 1, B3). Widening the DEFAULT rather than three
     * call sites is what stops that recurring: a fourth host inherits the right list.
     */
    @Input() accept = ABCP_INGEST_ACCEPT;

    @Input() maxSizeBytes = DEFAULT_MAX_SIZE_BYTES;

    /** The container is uploading: the primary button is disabled and progress shows. */
    @Input() pending = false;

    /** Per-file failures reported BACK by the container — ADR-0009 §11's "Teilfehler" state. */
    @Input() failures: UploadFailure[] = [];

    /** Only the ACCEPTED files. Rejected ones are shown and deliberately left out. */
    @Output() upload = new EventEmitter<File[]>();

    @Output() removeFile = new EventEmitter<number>();

    @Output() cancel = new EventEmitter<void>();

    /**
     * The current selection. Internal by necessity, not by preference: `File` objects are created
     * by the browser's file dialog, so no container can hand them in as an `@Input()`. Same
     * category as `document-detail`'s `pickerSearch` — transient widget state.
     */
    files: File[] = [];

    /** Locally refused picks, shown with a reason. Never part of `upload(File[])`. */
    rejected: RejectedUpload[] = [];

    /** Drag-over highlight. Purely visual, nothing upstream owns "a pointer is over the dropzone". */
    isDragOver = false;

    get hasSelection(): boolean {
        return this.files.length > 0;
    }

    /** Nothing to send, or a send already running. */
    get isSubmitDisabled(): boolean {
        return !this.hasSelection || this.pending;
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    /**
     * ⚠️ A drop MUST run the same validation as the file dialog. The older
     * `aumico-components/upload` component skips it on drop — that is the bug this one must not
     * repeat, because dropping is how a non-PDF would otherwise walk straight in.
     */
    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
        this.addFiles(event.dataTransfer?.files ?? null);
    }

    onFilesPicked(input: HTMLInputElement): void {
        this.addFiles(input.files);
        // Without this the same file picked twice in a row fires no `change` event.
        input.value = '';
    }

    onRemove(index: number): void {
        this.files = this.files.filter((_file, i) => i !== index);
        this.removeFile.emit(index);
    }

    onSubmit(): void {
        if (this.isSubmitDisabled) return;
        this.upload.emit(this.files);
    }

    onCancel(): void {
        this.cancel.emit();
    }

    /** Bytes as the list shows them — the view formats, it never re-times or re-scales upstream. */
    formatSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    /**
     * Accepted files stay selected; refused ones move to `rejected` WITH their reason. In
     * single-file mode a new pick replaces rather than appends.
     */
    private addFiles(list: FileList | null): void {
        if (!list?.length) return;

        const picked = Array.from(list);
        const accepted: File[] = [];
        const rejected: RejectedUpload[] = [];

        for (const file of picked) {
            const reason = this.rejectionReason(file);
            if (reason) rejected.push({ filename: file.name, size: file.size, reason });
            else accepted.push(file);
        }

        this.files = this.multiple ? [...this.files, ...accepted] : accepted.slice(0, 1);
        this.rejected = this.multiple ? [...this.rejected, ...rejected] : rejected;
    }

    /** Type first: an oversized non-PDF is refused for being the wrong kind, not the wrong size. */
    private rejectionReason(file: File): UploadRejectionReason | null {
        if (!this.isAcceptedType(file)) return 'type';
        if (this.maxSizeBytes && file.size > this.maxSizeBytes) return 'size';
        return null;
    }

    /**
     * Matches `accept` against the MIME type, with a filename-extension fallback: some browsers
     * hand over an empty `type` for a file dragged from certain sources.
     */
    private isAcceptedType(file: File): boolean {
        const patterns = this.accept
            .split(',')
            .map((entry) => entry.trim().toLowerCase())
            .filter(Boolean);
        if (!patterns.length) return true;

        const mime = (file.type || '').toLowerCase();
        const name = file.name.toLowerCase();

        return patterns.some((pattern) => {
            if (pattern.startsWith('.')) return name.endsWith(pattern);
            if (pattern.endsWith('/*')) return mime.startsWith(pattern.slice(0, -1));
            if (mime) return mime === pattern;
            // No MIME from the browser. The EXTENSION patterns above answer this case — every type
            // in `ABCP_INGEST_ACCEPT` carries both spellings for exactly that reason. This branch
            // used to hardcode `application/pdf`, which silently refused a dragged Excel file even
            // after the list had widened (Code-Review Runde 1, B3).
            return false;
        });
    }
}
